import api from './api';

export interface CompanySettings {
  id: string;
  companyName: string;
  companyCode?: string | null;
  legalName?: string | null;
  description?: string | null;
  // A web address, or a small image as a data URL
  logo?: string | null;
  address: string;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  country?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  website?: string | null;
  gstNumber?: string | null;
  panNumber?: string | null;
  tanNumber?: string | null;
  cinNumber?: string | null;
  bankName?: string | null;
  accountHolderName?: string | null;
  accountNumber?: string | null;
  ifscCode?: string | null;
  bankBranch?: string | null;
  swiftCode?: string | null;
  currency: string;
  language: string;
  timezone: string;
  // 4 = April
  financialYearStartMonth: number;
  // Days a new invoice is due in when the customer has no terms of its own
  paymentTermDays: number;
  // 'warn' lets an order over the credit limit through, 'block' refuses it
  creditLimitAction?: string;
  // Purchase orders above this amount need the higher approval; 0 for no limit
  purchaseApprovalLimit?: number;
  invoiceTerms?: string | null;
  invoiceNotes?: string | null;
  additionalSettings?: Record<string, any>;
  isActive: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

// What is sent when saving: an empty text clears an optional field
export type CompanySettingsInput = Partial<
  Omit<CompanySettings, 'id' | 'isActive' | 'createdBy' | 'updatedBy' | 'createdAt' | 'updatedAt' | 'additionalSettings'>
>;

export type CreateCompanySettingsDto = CompanySettingsInput & { companyName: string; address: string };
export type UpdateCompanySettingsDto = CompanySettingsInput;

export interface CompanySettingsOverview {
  // null until the company has been set up
  settings: CompanySettings | null;
  // The primary company of the master data, offered as a starting point
  primaryCompany: Record<string, any> | null;
  // Where a new invoice takes its seller from right now
  sellerSource: 'settings' | 'company' | 'last_invoice' | 'none';
  invoiceCount: number;
}

export interface InvoiceCompanySettings {
  companyName: string;
  logo?: string;
  address: string;
  city?: string;
  state?: string;
  pincode?: string;
  phone?: string;
  email?: string;
  gstNumber?: string;
  panNumber?: string;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
}

class CompanySettingsService {
  async getOverview(): Promise<CompanySettingsOverview> {
    const response = await api.get('/company-settings/overview');
    return response.data;
  }

  async getSettings(): Promise<CompanySettings> {
    const response = await api.get('/company-settings');
    return response.data;
  }

  async createSettings(data: CreateCompanySettingsDto): Promise<CompanySettings> {
    const response = await api.post('/company-settings', data);
    return response.data;
  }

  // Saves the settings; the first save creates them
  async updateSettings(data: UpdateCompanySettingsDto): Promise<CompanySettings> {
    const response = await api.put('/company-settings', data);
    return response.data;
  }

  async getSettingsForInvoice(): Promise<InvoiceCompanySettings> {
    const response = await api.get('/company-settings/invoice');
    return response.data;
  }
}

const companySettingsService = new CompanySettingsService();
export default companySettingsService;
